export const TILE = 24;
export const MAZE = [
  '###################',
  '#........#........#',
  '#.##.###.#.###.##.#',
  '#.................#',
  '#.##.#.#####.#.##.#',
  '#....#...#...#....#',
  '###.###.....###.###',
  '#....#...#...#....#',
  '#.##.#.#####.#.##.#',
  '#.................#',
  '#.##.###.#.###.##.#',
  '#........#........#',
  '###################',
];
export const ARENA = { width: MAZE[0].length * TILE, height: MAZE.length * TILE, speed: 114, radius: 9 };
export const PICKUPS = [
  { id: 'token', label: 'Tokens', points: 10, color: '#8ee9ff' },
  { id: 'data', label: 'Datasets', points: 15, color: '#77a9ff' },
  { id: 'memory', label: 'Memory', points: 20, color: '#cfb0ff' },
  { id: 'model', label: 'Model chip', points: 25, color: '#ffe7a0' },
];
export const DIRECTIONS = { up: [0, -1], left: [-1, 0], down: [0, 1], right: [1, 0] };
export const HIGH_SCORE_KEY = 'ty0x7-echo-data-run-best-v1';
const enemyColors = ['#ff798e', '#eeb0ff', '#ffc878', '#83f4d7'];
const opposite = { up: 'down', down: 'up', left: 'right', right: 'left' };
const point = (column, row) => ({ x: (column + .5) * TILE, y: (row + .5) * TILE });
const cell = agent => ({ column: Math.floor(agent.x / TILE), row: Math.floor(agent.y / TILE) });
const key = (column, row) => `${column},${row}`;
const isPath = (maze, column, row) => maze[row]?.[column] === '.';

export function neighbors(maze, column, row) {
  return Object.entries(DIRECTIONS).filter(([, [dx, dy]]) => isPath(maze, column + dx, row + dy))
    .map(([direction, [dx, dy]]) => ({ direction, column: column + dx, row: row + dy }));
}

export function routeDistances(maze, column, row) {
  const distances = new Map([[key(column, row), 0]]);
  const queue = [{ column, row }];
  for (let index = 0; index < queue.length; index++) {
    const current = queue[index];
    for (const next of neighbors(maze, current.column, current.row)) {
      const id = key(next.column, next.row);
      if (!distances.has(id)) { distances.set(id, distances.get(key(current.column, current.row)) + 1); queue.push(next); }
    }
  }
  return distances;
}

function pellets(run) {
  const items = [];
  const start = cell(run.spawn);
  run.maze.forEach((line, row) => [...line].forEach((value, column) => {
    if (value !== '.' || (column === start.column && row === start.row)) return;
    const corner = (column === 1 || column === run.maze[0].length - 2) && (row === 1 || row === run.maze.length - 2);
    const type = corner ? 3 : items.length % 13 === 0 ? 2 : items.length % 7 === 0 ? 1 : 0;
    items.push({ id: key(column, row), type, ...point(column, row) });
  }));
  return items;
}

const newPlayer = spawn => ({ ...spawn, direction: 'down', travelDirection: null, queued: null, target: null, moving: false });
const resetEnemy = (enemy, elapsed = 0) => ({ ...enemy, ...enemy.spawn, target: null, inactiveUntil: elapsed + 1.5 });

export function createRun({ portrait = false, wide = false } = {}) {
  const maze = portrait ? [...MAZE[0]].map((_, column) => MAZE.map(line => line[column]).join(''))
    : wide ? MAZE.map((line, row) => line.slice(0, -1) + ([3, 9].includes(row) ? '.' : '#') + line.slice(1)) : MAZE;
  const orient = (column, row) => portrait ? point(row, column) : point(wide && column === 17 ? 35 : column, row);
  const spawn = wide && !portrait ? point(18, 3) : orient(9, 6);
  const run = {
    maze, width: maze[0].length * TILE, height: maze.length * TILE, spawn,
    player: newPlayer(spawn), elapsed: 0, score: 0, collected: 0, combo: 0, lastPickup: -10,
    lives: 3, level: 1, powerUntil: 0, invulnerableUntil: 2, ended: false,
    enemies: [[1, 3], [17, 3], [1, 9], [17, 9]].map(([column, row], id) => {
      const home = orient(column, row);
      return { id, ...home, spawn: home, target: null, direction: 'down', color: enemyColors[id], inactiveUntil: 1.5 };
    }),
    notice: '', noticeUntil: 0,
  };
  run.pickups = pellets(run);
  return run;
}

// Move between corridor centers. Turns buffer until an intersection is reached.
function advance(agent, distance, choose) {
  const next = { ...agent, moving: false };
  for (let count = 0; distance > .0001 && count < 8; count++) {
    if (!next.target) {
      const target = choose(next);
      if (!target) break;
      next.target = point(target.column, target.row);
      next.direction = target.direction;
    }
    const dx = next.target.x - next.x, dy = next.target.y - next.y;
    const length = Math.hypot(dx, dy);
    const amount = Math.min(length, distance);
    if (length > .0001) { next.x += dx / length * amount; next.y += dy / length * amount; next.moving = true; }
    distance -= amount;
    if (length <= amount + .0001) { next.x = next.target.x; next.y = next.target.y; next.target = null; }
  }
  return next;
}

export function stepRun(previous, direction, seconds) {
  if (previous.ended) return previous;
  const dt = Number.isFinite(seconds) ? Math.min(Math.max(seconds, 0), .05) : 0;
  if (!dt) return previous;
  const run = { ...previous, elapsed: previous.elapsed + dt, player: { ...previous.player } };
  if (DIRECTIONS[direction]) run.player.queued = direction;
  run.player = advance(run.player, ARENA.speed * dt, agent => {
    const { column, row } = cell(agent), options = neighbors(run.maze, column, row);
    const turn = options.find(item => item.direction === agent.queued);
    if (turn) { agent.travelDirection = turn.direction; agent.queued = null; return turn; }
    return options.find(item => item.direction === agent.travelDirection);
  });
  if (run.elapsed - run.lastPickup > 4) run.combo = 0;
  run.pickups = previous.pickups.filter(item => {
    if (Math.hypot(item.x - run.player.x, item.y - run.player.y) > 9) return true;
    run.combo = Math.min(4, run.combo + 1);
    const points = PICKUPS[item.type].points * run.combo;
    run.score += points; run.collected++; run.lastPickup = run.elapsed;
    if (item.type === 3) run.powerUntil = run.elapsed + 7;
    run.notice = `+${points}`; run.noticeUntil = run.elapsed + .8;
    return false;
  });
  const location = cell(run.player), distances = routeDistances(run.maze, location.column, location.row);
  const powered = run.powerUntil > run.elapsed;
  run.enemies = previous.enemies.map(enemy => {
    if (enemy.inactiveUntil > run.elapsed) return enemy;
    return advance(enemy, (powered ? 40 : Math.min(98, 62 + enemy.id * 4 + (run.level - 1) * 5)) * dt, agent => {
      const { column, row } = cell(agent);
      let options = neighbors(run.maze, column, row);
      const forward = options.filter(option => option.direction !== opposite[agent.direction]);
      if (forward.length) options = forward;
      // Different tie breaks separate pursuers; shortest paths cannot cross walls.
      const order = Object.keys(DIRECTIONS);
      options.sort((a, b) => {
        const delta = distances.get(key(a.column, a.row)) - distances.get(key(b.column, b.row));
        return (powered ? -delta : delta) || ((order.indexOf(a.direction) + enemy.id) % 4 - (order.indexOf(b.direction) + enemy.id) % 4);
      });
      return options[0];
    });
  });
  for (let index = 0; index < run.enemies.length; index++) {
    const enemy = run.enemies[index];
    if (enemy.inactiveUntil > run.elapsed || Math.hypot(enemy.x - run.player.x, enemy.y - run.player.y) > 16) continue;
    if (powered) {
      run.score += 100; run.notice = '+100'; run.noticeUntil = run.elapsed + .8;
      run.enemies[index] = resetEnemy(enemy, run.elapsed);
    } else if (run.invulnerableUntil <= run.elapsed) {
      run.lives--; run.combo = 0; run.ended = run.lives === 0;
      run.player = newPlayer(run.spawn);
      run.invulnerableUntil = run.elapsed + 2;
      run.enemies = run.enemies.map(item => resetEnemy(item, run.elapsed));
      run.notice = '-1 LIFE'; run.noticeUntil = run.elapsed + 1;
      break;
    }
  }
  if (!run.ended && !run.pickups.length) {
    run.level++; run.score += 250; run.pickups = pellets(run);
    run.player = newPlayer(run.spawn); run.invulnerableUntil = run.elapsed + 2;
    run.enemies = run.enemies.map(enemy => resetEnemy(enemy, run.elapsed));
    run.notice = `LEVEL ${run.level}`; run.noticeUntil = run.elapsed + 2;
  }
  return run;
}

export function readHighScore() {
  try {
    const value = Number(window.localStorage.getItem(HIGH_SCORE_KEY));
    return Number.isSafeInteger(value) && value >= 0 ? value : 0;
  } catch { return 0; }
}

export function saveHighScore(score) {
  try { window.localStorage.setItem(HIGH_SCORE_KEY, String(score)); } catch { /* Play remains available without storage. */ }
}
